import { ASPECTS } from './schemas.js';
/** Reference pictures are used only when the shot starts from no picture (another shot's frame, or its storyboard frame). */
export function promptMode(shot) {
    return shot.pictures.length > 0 && !shot.startFrom && !startsFromStoryboard(shot) ? 'references' : 'text';
}
/** The video starts from the shot's approved storyboard frame (a shot that starts from another shot keeps doing that). */
export function startsFromStoryboard(shot) {
    return !shot.startFrom && !!shot.frame?.approved;
}
const SECTION_SEP = '\n\n';
/** Trims and makes sure the text ends like a sentence. */
export function sentence(s) {
    const t = s.trim().replace(/\s+/g, ' ');
    if (!t)
        return '';
    // Ends with spoken words or with the place they go: they end the sentence themselves.
    return /[.!?…:;]["')\]]?$/.test(t) || /<\/d>$/.test(t) || /\{dialogue\}$/i.test(t) ? t : `${t}.`;
}
function capitalise(s) {
    return s ? s[0].toUpperCase() + s.slice(1) : s;
}
/** 5.5 → "00:05.500" */
export function cutTime(seconds) {
    const ms = Math.round(seconds * 1000);
    const m = Math.floor(ms / 60_000);
    const s = Math.floor((ms % 60_000) / 1000);
    const rest = ms % 1000;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}.${String(rest).padStart(3, '0')}`;
}
/** Words inside <d>…</d>: no double quotes (they would be drawn on screen), one line. */
export function spokenText(s) {
    return s
        .replace(/["“”„]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}
/** Speaker IDs: the project's cast first, then any other character as it first appears in the shot. */
export function speakerIds(shot, castIds) {
    const ids = new Map();
    for (const c of castIds)
        if (!ids.has(c))
            ids.set(c, ids.size + 1);
    for (const m of shot.cast)
        if (m.type === 'character' && !ids.has(m.id))
            ids.set(m.id, ids.size + 1);
    for (const l of shot.lines)
        if (l.characterId && !ids.has(l.characterId))
            ids.set(l.characterId, ids.size + 1);
    return ids;
}
/**
 * A short tag the video model can SEE, used instead of a name wherever a character is referred to again or speaks:
 * "the man in a black faux-leather jacket", "the chubby salmon-pink cartoon stomach". A name ("Vikram") means
 * nothing on screen: on a real shot the model gave a bully's line to the waiter, the last person it had seen
 * described before "Vikram says".
 */
export function visualTag(c) {
    const d = c.visualDescription.trim().replace(/\s+/g, ' ');
    if (!d)
        return c.callName || c.name;
    const noun = /\b(woman|female|girl|lady)\b/i.test(d)
        ? /\bgirl\b/i.test(d) ? 'girl' : 'woman'
        : /\b(man|male|boy|guy)\b/i.test(d)
            ? /\bboy\b/i.test(d) ? 'boy' : 'man'
            : '';
    const wearing = /\bwearing (?:an? |the )?([^,.;]+?)(?:\s+(?:over|under|with|and)\b|[,.;]|$)/i.exec(d)?.[1]?.trim();
    if (noun && wearing)
        return `the ${noun} in ${/^(an?|the) /i.test(wearing) ? '' : /^[aeiou]/i.test(wearing) ? 'an ' : 'a '}${wearing}`.replace(/\s+/g, ' ');
    // Cartoon/mascot or no clothing given: the description up to "with/holding/who/shaped…" (commas inside an
    // adjective list stay: "a chubby, glossy salmon-pink cartoon stomach"), at most 8 words, as "the …".
    const head = d
        .split(/\s+(?:with|holding|who|that|shaped|standing|sitting|wearing)\b|[;.(]/i)[0]
        .trim()
        .replace(/^(an?|the)\s+/i, '')
        .replace(/[,\s]+$/, '');
    return `the ${head.split(' ').slice(0, 8).join(' ').replace(/[,\s]+$/, '')}`;
}
/** Two or more characters on screen: only then can the model mix up who is who, so only then are tags used. */
function crowded(shot, ctx) {
    return shot.cast.filter((m) => m.type === 'character' && ctx.characters[m.id]).length >= 2;
}
function entityName(type, id, ctx) {
    if (type === 'product')
        return ctx.products[id]?.title ?? 'the product';
    const c = ctx.characters[id];
    return c ? c.callName || c.name : 'someone';
}
/**
 * Replaces [Name] tokens (a character of the shot, matched by name or call name; [Product] or the product's
 * title for the product). References mode: <Subject n>. Text mode: the first mention gets the full visual
 * description (plus the speaker ID if the character speaks in the shot), later mentions the call name.
 */
function replaceTokens(textIn, shot, ctx, r, seen) {
    return textIn.replace(/\[([^\][]{1,80})\]/g, (whole, raw) => {
        const key = raw.trim().toLowerCase();
        const member = shot.cast.find((m) => {
            if (m.type === 'product') {
                const p = ctx.products[m.id];
                return key === 'product' || (p && p.title.toLowerCase() === key);
            }
            const c = ctx.characters[m.id];
            return !!c && (c.name.toLowerCase() === key || (!!c.callName && c.callName.toLowerCase() === key));
        });
        if (!member)
            return whole;
        if (r.mode === 'references') {
            const n = r.subjectOf(member.type, member.id);
            return n ? `<Subject ${n}>` : whole;
        }
        const tag = `${member.type}:${member.id}`;
        const first = !seen.has(tag);
        seen.add(tag);
        if (member.type === 'product') {
            const p = ctx.products[member.id];
            return first ? member.note || p?.promptDescription || p?.title || 'the product' : (p?.title ?? 'the product');
        }
        const c = ctx.characters[member.id];
        if (!c)
            return whole;
        if (!first)
            return crowded(shot, ctx) ? visualTag(c) : c.callName || c.name;
        const sid = r.speaking.has(c.id) ? r.speakers.get(c.id) : undefined;
        const desc = member.note || c.visualDescription || c.callName || c.name;
        return sid ? `${desc} (S${sid})` : desc;
    });
}
function lineText(line, ctx, r, defaultLanguage) {
    let who;
    if (line.characterId) {
        const c = ctx.characters[line.characterId];
        const subject = r.mode === 'references' ? r.subjectOf('character', line.characterId) : null;
        if (subject)
            who = `<Subject ${subject}>`;
        else {
            const sid = r.speakers.get(line.characterId);
            // Seen, not named (when others are on screen): the speaker is the person the model can identify.
            const name = c ? (r.crowded ? visualTag(c) : c.callName || c.name) : line.speakerName || 'A voice';
            who = `${capitalise(name)}${sid ? ` (S${sid})` : ''}`;
        }
    }
    else {
        who = capitalise(line.speakerName.trim() || 'A voice');
    }
    const voice = (line.voice ||
        (line.characterId ? ctx.characters[line.characterId]?.voiceDescription : '') ||
        '').trim();
    const delivery = (line.delivery || 'says').trim();
    const language = (line.language || defaultLanguage || 'Hindi').trim();
    return `${who}${voice ? `, ${voice},` : ''} ${delivery}: <d>[${language}] ${spokenText(line.text)}</d>`;
}
/** Puts the spoken lines of one part where {dialogue} stands, or after the part's text. */
function withLines(partText, lines) {
    const joined = lines.join(' ');
    if (/\{dialogue\}/i.test(partText))
        return partText.replace(/\{dialogue\}/gi, joined).trim();
    return [partText.trim(), joined].filter(Boolean).join(' ');
}
function resolve(shot, ctx) {
    const mode = promptMode(shot);
    const speaking = new Set(shot.lines.map((l) => l.characterId).filter((x) => !!x));
    return {
        mode,
        speakers: speakerIds(shot, ctx.castIds),
        speaking,
        crowded: crowded(shot, ctx),
        subjectOf: (type, id) => {
            const i = shot.cast.findIndex((m) => m.type === type && m.id === id);
            return i >= 0 ? i + 1 : null;
        },
    };
}
/** The description: "[Shot 1] <style>, <look>, <frame>. <picture> <camera> <lines> [Shot 2] … No subtitles…" */
export function buildDescription(shot, ctx) {
    const r = resolve(shot, ctx);
    const seen = new Set();
    const style = (shot.style || ctx.style).trim().replace(/[.,]$/, '');
    const head = `[Shot 1] ${[style, shot.look.trim().replace(/[.,]$/, ''), ASPECTS[ctx.aspect].frame].filter(Boolean).join(', ')}.`;
    const linesFor = (cut) => shot.lines.filter((l) => (l.cut ?? 0) === cut && l.text.trim()).map((l) => lineText(l, ctx, r, ctx.language));
    // Lines of a cut that does not exist stay with the last part that does.
    const cuts = [...shot.cuts].sort((a, b) => a.at - b.at);
    const orphan = shot.lines
        .filter((l) => (l.cut ?? 0) > cuts.length && l.text.trim())
        .map((l) => lineText(l, ctx, r, ctx.language));
    const opening = [
        sentence(replaceTokens(shot.picture, shot, ctx, r, seen)),
        sentence(replaceTokens(shot.camera, shot, ctx, r, seen)),
    ]
        .filter(Boolean)
        .join(' ');
    const parts = [withLines(`${head} ${opening}`, [...linesFor(0), ...(cuts.length ? [] : orphan)])];
    cuts.forEach((cut, i) => {
        const desc = sentence(replaceTokens(cut.description, shot, ctx, r, seen)).replace(/^./, (c) => c.toLowerCase());
        const extra = i === cuts.length - 1 ? orphan : [];
        parts.push(withLines(`[Shot ${i + 2}] At ${cutTime(cut.at)}, the camera cuts to ${desc}`, [...linesFor(i + 1), ...extra]));
    });
    const ending = shot.noOnScreenText ? 'No subtitles, no on-screen text.' : 'No subtitles or captions.';
    return capitaliseSentences(`${parts.join(' ')} ${whoSpeaks(shot, ctx, r)} ${ending}`.replace(/\s+/g, ' ').trim());
}
/**
 * Says who talks and who does not: "Only the man in a black jacket (S1) speaks in this shot; the man in a
 * mustard-yellow polo stays silent with his mouth closed." Without it the model lets other people in the frame
 * speak or laugh the lines (a real shot gave the bully's insult and laugh to the waiter standing next to him).
 */
export function whoSpeaks(shot, ctx, r = resolve(shot, ctx)) {
    const characters = shot.cast.filter((m) => m.type === 'character' && ctx.characters[m.id]);
    if (!r.crowded || !shot.lines.some((l) => l.text.trim()))
        return '';
    const label = (id) => {
        if (r.mode === 'references') {
            const n = r.subjectOf('character', id);
            if (n)
                return `<Subject ${n}>`;
        }
        return visualTag(ctx.characters[id]);
    };
    const talking = characters.filter((m) => r.speaking.has(m.id)).map((m) => {
        const sid = r.speakers.get(m.id);
        return `${label(m.id)}${r.mode === 'references' || !sid ? '' : ` (S${sid})`}`;
    });
    const silent = characters.filter((m) => !r.speaking.has(m.id)).map((m) => label(m.id));
    const parts = [];
    if (talking.length)
        parts.push(`Only ${listJoin(talking)} ${talking.length > 1 ? 'speak' : 'speaks'} in this shot`);
    if (silent.length)
        parts.push(`${listJoin(silent)} ${silent.length > 1 ? 'stay silent with their mouths closed' : 'stays silent with the mouth closed'}`);
    return parts.length ? sentence(capitalise(parts.join('; '))) : '';
}
/** The picture numbers (1-based) that show a cast member: its own `picture`, and every picture that is of it. */
export function picturesOf(shot, m) {
    const out = new Set();
    if (m.picture && m.picture <= shot.pictures.length)
        out.add(m.picture);
    shot.pictures.forEach((p, i) => {
        if ((p.type === 'character' && m.type === 'character' && p.characterId === m.id) || (p.type === 'product' && m.type === 'product' && p.productId === m.id))
            out.add(i + 1);
    });
    return [...out].sort((a, b) => a - b);
}
function pictureList(nums) {
    if (nums.length > 1 && nums.every((n, i) => i === 0 || n === nums[i - 1] + 1))
        return `<Picture ${nums[0]}> to <Picture ${nums[nums.length - 1]}>`;
    return listJoin(nums.map((n) => `<Picture ${n}>`));
}
function subjectDefinitions(shot, ctx) {
    return shot.cast
        .map((m, i) => {
        let desc;
        if (m.type === 'product') {
            const p = ctx.products[m.id];
            desc = m.note || p?.promptDescription || p?.title || 'the product';
        }
        else {
            const c = ctx.characters[m.id];
            desc = m.note || c?.visualDescription || c?.callName || c?.name || 'a character';
        }
        desc = desc.trim().replace(/\.$/, '');
        const nums = picturesOf(shot, m);
        const pic = nums.length ? ` shown in ${pictureList(nums)}` : '';
        return `<Subject ${i + 1}> is ${desc}${pic}.`;
    })
        .join(' ');
}
/**
 * Who is who in a shot with two or more characters. A first frame alone shows people but not WHICH is Vikram, so
 * the video model guesses who speaks (FreeVideo: a first frame and reference pictures cannot go together). Such a
 * shot is made from reference pictures instead: every character's pictures (speakers first), the product's
 * picture, then `scene` — the shot's approved still, or the frame it continues from — as the place and layout.
 * Lines are then spoken by <Subject n>, tied to that person's pictures.
 *
 * pictureCount: how many pictures each character has (its cast pictures, at most 3 used).
 * Returns the shot to generate (references mode), or null when the shot needs no character references.
 */
export function withCharacterReferences(shot, ctx, pictureCount, scene, productPicture) {
    const chars = shot.cast.filter((m) => m.type === 'character' && ctx.characters[m.id]);
    const pictured = chars.filter((m) => (pictureCount[m.id] ?? 0) > 0);
    if (chars.length < 2 || !pictured.length)
        return null;
    const speaking = new Set(shot.lines.map((l) => l.characterId));
    const order = [...pictured].sort((a, b) => Number(speaking.has(b.id)) - Number(speaking.has(a.id)));
    const pictures = [];
    const room = MAX_SENT_PICTURES - (scene ? 1 : 0) - (productPicture ? 1 : 0);
    // Each person gets at least one picture before anyone gets a second.
    const per = Math.max(1, Math.min(3, Math.floor(room / order.length)));
    for (const m of order)
        for (let k = 0; k < Math.min(per, pictureCount[m.id] ?? 0); k++)
            pictures.push({ type: 'character', characterId: m.id, imageIndex: k });
    const product = shot.cast.find((m) => m.type === 'product');
    if (product && productPicture)
        pictures.push({ type: 'product', productId: product.id });
    if (scene)
        pictures.push(scene);
    return {
        ...shot,
        startFrom: null,
        frame: null,
        pictures,
        cast: shot.cast.map((m) => ({ ...m, picture: null })),
    };
}
/** Reference pictures one generation may carry (FreeVideo accepts up to 32; each costs memory and time). */
export const MAX_SENT_PICTURES = 10;
function listJoin(items) {
    if (items.length <= 1)
        return items.join('');
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}
function defaultRetention(shot) {
    const withPicture = shot.cast
        .map((m, i) => ({ subject: `<Subject ${i + 1}>`, nums: picturesOf(shot, m) }))
        .filter((x) => x.nums.length > 0);
    const used = new Set(withPicture.flatMap((x) => x.nums));
    const parts = [];
    if (withPicture.length) {
        const all = [...used].sort((a, b) => a - b);
        parts.push(`Keep ${listJoin(withPicture.map((x) => x.subject))} exactly as in ${pictureList(all)}: shapes, colours, faces, clothes, labels and the visual style.`);
    }
    const scenes = shot.pictures.map((_, i) => i + 1).filter((p) => !used.has(p));
    if (scenes.length)
        parts.push(`Keep the setting and look of ${listJoin(scenes.map((p) => `<Picture ${p}>`))}.`);
    parts.push('Everything else is new.');
    return parts.join(' ');
}
function defaultSummary(shot, ctx, r) {
    const style = (shot.style || ctx.style).trim().replace(/[.,]$/, '');
    const picture = replaceTokens(shot.picture, shot, ctx, r, new Set())
        .replace(/\{dialogue\}/gi, '')
        .trim();
    const first = picture.split(/(?<=[.!?])\s+/)[0] ?? picture;
    return `${style}: ${sentence(first)}`;
}
export function buildPrompt(shot, ctx) {
    const r = resolve(shot, ctx);
    const sound = sentence(shot.sound) || 'A soft, quiet room tone.';
    const music = sentence(shot.music) || 'N/A';
    const description = buildDescription(shot, ctx);
    if (r.mode === 'text') {
        return [
            `integrated_multimodal_description: ${description}`,
            `overall_soundscape: ${sound}`,
            `non_diegetic_music: ${music}`,
        ].join(SECTION_SEP);
    }
    const summary = sentence(replaceTokens(shot.summary, shot, ctx, r, new Set())) || defaultSummary(shot, ctx, r);
    const retention = sentence(replaceTokens(shot.retention, shot, ctx, r, new Set())) || defaultRetention(shot);
    return [
        `subject_definitions: ${subjectDefinitions(shot, ctx)}`,
        `summary: ${summary}`,
        `retention_analysis: ${retention}`,
        `detailed_description: ${description}`,
        `overall_soundscape: ${sound}`,
        `non_diegetic_music: ${music}`,
    ].join(SECTION_SEP);
}
/** Things worth telling the user about a shot before it is made. */
export function lintShot(shot, ctx) {
    const out = [];
    if (shot.startFrom && shot.pictures.length)
        out.push('This shot starts from a frame, so its reference pictures are not used.');
    else if (startsFromStoryboard(shot) && shot.pictures.length)
        out.push('This shot starts from its approved storyboard frame, so its reference pictures are not used.');
    if (shot.noOnScreenText && /"[^"]+"/.test(shot.picture))
        out.push('Words in double quotes in the picture text may be drawn on screen (fine for a label or sign).');
    for (const l of shot.lines) {
        if (!l.text.trim())
            out.push('A dialogue line is empty.');
        const lang = (l.language || ctx.language).toLowerCase();
        if (lang === 'hindi' && l.text.trim() && !/[ऀ-ॿ]/.test(l.text))
            out.push('A Hindi line is not in Devanagari script.');
    }
    if (/\b(the others|everyone|everybody|all of them|they all|the rest)\b[^.]{0,40}\b(laugh|giggl|snort|smil|chuckl|mock)/i.test(`${shot.picture} ${shot.camera}`))
        out.push('"The others laugh" lets anyone in the frame laugh — even the person being mocked. Name who laughs.');
    if (new Set(shot.lines.map((l) => l.characterId ?? l.speakerName)).size > 1)
        out.push('More than one person speaks in this shot; the model may mix up who says what. One speaker per shot is safest.');
    for (const m of shot.cast)
        if (m.picture && m.picture > shot.pictures.length)
            out.push(`A subject points at picture ${m.picture}, which is not there.`);
    const spoken = shot.lines.reduce((n, l) => n + l.text.trim().split(/\s+/).filter(Boolean).length, 0);
    // The Fat Cutter ad fitted up to about 3.3 Hindi words a second (shot 01: 20 words in 6 s).
    // Above ~3.6 the model rushes or cuts the line.
    if (spoken > shot.seconds * 3.6)
        out.push(`About ${spoken} words may not fit in ${shot.seconds} s; make the shot longer or the lines shorter.`);
    return [...new Set(out)];
}
/** FreeVideo snaps the length to 24 fps frames on a 17n+5 grid, rounding up: 6 s → 6.583 s. */
export function actualSeconds(seconds) {
    const frames = Math.max(5, seconds * 24);
    const n = Math.ceil((frames - 5) / 17 - 1e-9);
    return (17 * n + 5) / 24;
}
/**
 * A sentence must not start in lower case (a character's description put in at the start of a sentence:
 * "a chubby cartoon stomach holds…"). Spoken words inside <d>…</d> are left exactly as written.
 */
export function capitaliseSentences(text) {
    return text
        .split(/(<d>[\s\S]*?<\/d>)/)
        .map((part, i) => i % 2 === 1
        ? part
        : part.replace(/(^|[.!?]\s+|\]\s+)([a-z])/g, (_, before, c) => before + c.toUpperCase()))
        .join('');
}
/* ---------- Storyboard stills (FreeVideo's image job) ---------- */
/** FreeVideo's image job takes a prompt of at most 2000 characters. */
export const IMAGE_PROMPT_MAX = 2000;
/** The characters seen in the shot's opening picture, in cast order: FreeVideo places at most 3. */
function framedCharacters(shot, ctx) {
    return shot.cast.filter((m) => m.type === 'character' && ctx.characters[m.id]).slice(0, 3);
}
/** "[Vikram] sits at the table on the left" → 0.25. Looks at the words after the character's [Name] in the picture text. */
function sideOf(shot, c) {
    const names = [c.name, c.callName].filter(Boolean).map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    if (!names.length)
        return null;
    const m = new RegExp(`\\[(?:${names.join('|')})\\]([^\\[.!?]{0,90})`, 'i').exec(shot.picture);
    const after = m?.[1] ?? '';
    if (/\bleft\b/i.test(after))
        return 0.25;
    if (/\bright\b/i.test(after))
        return 0.75;
    if (/\b(centre|center|middle)\b/i.test(after))
        return 0.5;
    return null;
}
/**
 * Where each character stands in the still. The speaker is bigger and lower in the frame (nearer the camera), so
 * the video model sees at once who talks. Sides written in the picture text ("on the left") are kept; otherwise
 * 1 person: centre; 2: 0.3 / 0.7; 3: 0.2 / 0.5 / 0.8.
 */
export function framePeople(shot, ctx) {
    const chars = framedCharacters(shot, ctx);
    const speaking = new Set(shot.lines.filter((l) => l.text.trim()).map((l) => l.characterId));
    const defaults = [[], [0.5], [0.3, 0.7], [0.2, 0.5, 0.8]][chars.length] ?? [];
    let xs = chars.map((m, i) => sideOf(shot, ctx.characters[m.id]) ?? defaults[i]);
    if (new Set(xs).size !== xs.length)
        xs = defaults;
    return chars.map((m, i) => {
        const talks = speaking.has(m.id);
        const solo = chars.length === 1;
        return {
            characterId: m.id,
            x: xs[i],
            height: solo ? 0.7 : talks ? 0.68 : 0.56,
            feet: solo ? 0.92 : talks ? 0.95 : 0.86,
            speaking: talks,
        };
    });
}
/** Cuts text to whole sentences that fit in `max` characters. */
function fitSentences(text, max) {
    if (text.length <= max)
        return text;
    const parts = text.split(/(?<=[.!?])\s+/);
    let out = '';
    for (const p of parts) {
        if ((out ? out.length + 1 : 0) + p.length > max)
            break;
        out = out ? `${out} ${p}` : p;
    }
    return out || text.slice(0, max);
}
/**
 * The prompt of a shot's storyboard still: the opening picture of the shot (before any camera cut), with the
 * speaker shown talking and everyone else visibly silent — so a wrong speaker is caught on a picture that costs
 * seconds, before a video that costs a minute.
 */
export function buildFramePrompt(shot, ctx, refs = {}) {
    // Text mode, and no speaker IDs: the still has no sound.
    const r = { ...resolve(shot, ctx), mode: 'text', speaking: new Set() };
    const style = (shot.style || ctx.style).trim().replace(/[.,]$/, '');
    const head = `A single still frame from a film: ${[style, shot.look.trim().replace(/[.,]$/, ''), ASPECTS[ctx.aspect].frame].filter(Boolean).join(', ')}.`;
    const seen = new Set();
    const picture = sentence(replaceTokens(shot.picture.replace(/\{dialogue\}/gi, ' '), shot, ctx, r, seen));
    const camera = sentence(replaceTokens(shot.camera, shot, ctx, r, seen));
    const chars = framedCharacters(shot, ctx);
    const speakingIds = new Set(shot.lines.filter((l) => l.text.trim() && (l.cut ?? 0) === 0).map((l) => l.characterId));
    const who = (id) => (chars.length > 1 ? visualTag(ctx.characters[id]) : (ctx.characters[id].callName || ctx.characters[id].name));
    const talk = chars.map((m) => speakingIds.has(m.id)
        ? `${capitalise(who(m.id))} is speaking, mouth open mid-word, the face clearly visible.`
        : chars.length > 1 && speakingIds.size
            ? `${capitalise(who(m.id))} listens with the mouth closed.`
            : '');
    // Reference pictures keep everyone looking the same in every still: "image 1" … in the order given.
    const refCharacters = (refs.characters ?? []).filter((id) => ctx.characters[id]);
    const identity = refCharacters.map((id, i) => `${capitalise(who(id))} is exactly the character in image ${i + 1}: the same face, hair, body, colours and clothes.`);
    let next = refCharacters.length + 1;
    if (refs.background) {
        const what = refs.background.description.trim().replace(/[.\s]+$/, '');
        identity.push(`The place is exactly the place in image ${next++}${what ? ` (${what})` : ''}: the same setting, layout, colours and lighting.`);
    }
    if (refs.product)
        identity.push(`The product looks exactly like image ${next}: the same shape, colours and label.`);
    const ending = shot.noOnScreenText ? 'No text, no captions, no subtitles, no watermark.' : 'No captions, no subtitles, no watermark.';
    const fixed = [head, camera, ...talk, ...identity, ending].filter(Boolean).join(' ');
    const room = IMAGE_PROMPT_MAX - fixed.length - 2;
    const body = [head, fitSentences(picture, Math.max(200, room)), camera, ...talk, ...identity, ending].filter(Boolean).join(' ');
    return capitaliseSentences(body.replace(/\s+/g, ' ').trim()).slice(0, IMAGE_PROMPT_MAX);
}
/** FreeVideo's image job takes at most 4 reference pictures. */
export const FRAME_MAX_REFERENCES = 4;
/** A side view of a character sheet, drawn from its front picture (image 1). */
export function buildSheetViewPrompt(view, style) {
    const look = style.trim().replace(/[.\s]+$/, '');
    const what = view === 'face'
        ? 'a close-up portrait of the head and shoulders, looking straight at the camera with a friendly neutral expression'
        : 'a full-body three-quarter view, the body turned slightly to the left, standing upright, the whole body from head to feet in view';
    return `Image 1 shows a character. Draw exactly the same character — the same face, hair, skin, body shape, colours and clothes — as ${what}. Plain light grey studio background, soft even lighting${look ? `, ${look}` : ''}. Only this one character; no other people, no text, no watermark.`.slice(0, IMAGE_PROMPT_MAX);
}
/** Changing a still: the current still goes in as image 1. */
export function buildFrameEditPrompt(instruction) {
    const what = instruction.trim().replace(/[.\s]+$/, '');
    return `Edit image 1: ${what}. Keep everything else in image 1 exactly the same: the people, faces, clothes, setting, lighting, framing and style. No text, no captions, no watermark.`.slice(0, IMAGE_PROMPT_MAX);
}
/**
 * A character's cast picture: full body on a plain backdrop, the kind FreeVideo's `people` option places into
 * storyboard stills and the video model uses as a reference.
 */
export function buildCastPrompt(c, style, opts = {}) {
    const desc = (c.visualDescription || c.callName || c.name).trim().replace(/[.\s]+$/, '');
    // The outfit picture (image 1) gives the clothes only: the person comes from the description.
    const outfit = opts.outfit
        ? ' The character wears exactly the clothes shown in image 1 — the same garments, colours, patterns and fit (only the clothes come from image 1, not the person or the background).'
        : '';
    return `Full-body character picture of ${desc}.${outfit} Standing upright and facing the camera, the whole body from head to feet in view, arms relaxed, a friendly neutral expression. Plain light grey studio background, soft even lighting. ${style.trim().replace(/[.\s]+$/, '')}. Only this one character; no other people, no text, no watermark.`.slice(0, IMAGE_PROMPT_MAX);
}
